/**
 * Detecção de autoria expandida para Minhas Obras
 *
 * Estende a detecção existente (authorshipDetection.ts) com suporte a:
 * - Nomes alternativos (alternativeNames)
 * - Detecção de orientações (detectOrientations)
 * - Construção de MinhaObra a partir de Work
 */

import { parseAcademicName, matchesAuthorName } from '../../utils/authorshipDetection';
import type { Work } from '../../types/work';
import type { MinhaObra, MinhaObraWorkType, MinhaObraDetectionSource } from './types';

interface UserBibliographicProfile {
  primaryName: string;
  alternativeNames: string[];
  detectOrientations: boolean;
}

/**
 * Mapeia WorkType do Bigwa para MinhaObraWorkType (Lattes)
 */
function mapWorkType(type: Work['type']): MinhaObraWorkType {
  switch (type) {
    case 'article': return 'artigo';
    case 'book': return 'livro';
    case 'chapter': return 'capitulo';
    case 'conference': return 'congresso';
    case 'thesis': return 'tese';
    case 'report': return 'relatorio';
    default: return 'outro';
  }
}

/**
 * Verifica se o nome do usuário aparece como orientador de uma tese
 */
function matchesAdvisor(
  advisor: string | undefined,
  profile: UserBibliographicProfile
): boolean {
  const trimmedAdvisor = advisor?.trim();
  if (!trimmedAdvisor || !profile.detectOrientations) return false;

  const allNames = [profile.primaryName, ...profile.alternativeNames].filter(Boolean);
  for (const userName of allNames) {
    const parsed = parseAcademicName(userName);
    if (!parsed.surname) continue;

    // Simular um author object para reusar matchesAuthorName
    const fakeAuthors: Work['authors'] = [{
      firstName: '',
      lastName: '',
      name: trimmedAdvisor,
    }];
    if (matchesAuthorName(fakeAuthors, parsed)) return true;
  }

  return false;
}

/**
 * Detecta se uma referência é de autoria do usuário
 * Suporta primaryName + alternativeNames + orientações
 */
export function detectAuthorship(
  reference: Work,
  profile: UserBibliographicProfile
): boolean {
  const allNames = [profile.primaryName, ...profile.alternativeNames].filter(Boolean);
  if (allNames.length === 0) return false;

  // Verificar autoria
  for (const userName of allNames) {
    const parsed = parseAcademicName(userName);
    if (!parsed.surname) continue;
    if (matchesAuthorName(reference.authors, parsed)) return true;
  }

  // Verificar orientação (se ativo)
  if (matchesAdvisor(reference.advisor, profile)) return true;

  return false;
}

/**
 * Constrói um MinhaObra a partir de um Work detectado
 */
export function buildMinhaObra(
  work: Work,
  source: MinhaObraDetectionSource
): MinhaObra {
  const authors = work.authors?.map(a => {
    if (a.lastName && a.firstName) return `${a.lastName}, ${a.firstName}`;
    if (a.lastName) return a.lastName;
    return a.name || '';
  }).filter(Boolean) || [];

  const advisorTrimmed = work.advisor?.trim() || '';
  const isOrientation = work.type === 'thesis' && advisorTrimmed.length > 0;

  // Editor: proteger contra firstName/lastName undefined
  const editorStr = work.editors?.map(e => {
    const last = e.lastName || '';
    const first = e.firstName || '';
    if (last && first) return `${last}, ${first}`;
    return last || first || '';
  }).filter(Boolean).join('; ') || undefined;

  return {
    id: crypto.randomUUID(),
    workId: work.id,
    status: source === 'lattes_import' || source === 'manual' ? 'confirmed' : 'pending',
    confirmedAt: source === 'lattes_import' || source === 'manual' ? new Date().toISOString() : undefined,
    detectedAt: new Date().toISOString(),
    detectionSource: source,
    title: work.title || '',
    authors,
    year: String(work.year || ''),
    type: mapWorkType(work.type),
    journal: work.journal,
    issn: work.issn,
    doi: work.doi,
    volume: work.volume,
    number: work.issue,
    pages: work.pages || work.chapterPages,
    publisher: work.publisher,
    isbn: work.isbn,
    editor: editorStr,
    booktitle: work.bookTitle,
    conferenceName: work.event,
    university: work.university || work.school,
    degree: work.thesisType,
    isOrientation,
    orientedStudent: isOrientation ? (authors[0] || advisorTrimmed) : undefined,
  };
}

/**
 * Varre a biblioteca inteira em busca de obras do usuário
 * Retorna apenas novas detecções (não duplica existentes)
 */
export function detectAuthorshipInLibrary(
  references: Work[],
  profile: UserBibliographicProfile,
  existingWorkIds: Set<string>
): MinhaObra[] {
  const detected: MinhaObra[] = [];

  for (const ref of references) {
    if (ref.isDeleted) continue;
    if (existingWorkIds.has(ref.id)) continue;
    if (detectAuthorship(ref, profile)) {
      detected.push(buildMinhaObra(ref, 'retroactive'));
    }
  }

  return detected;
}
